/**
 * Tiny structural JSON diff used for "before/after" preview on config saves.
 * Not a full RFC-7396 patch — just enough to show what changed.
 */

import { cn } from "../lib/cn";

type DiffOp =
  | { kind: "unchanged"; key: string; value: unknown }
  | { kind: "added"; key: string; value: unknown }
  | { kind: "removed"; key: string; value: unknown }
  | { kind: "changed"; key: string; before: unknown; after: unknown };

function diff(before: unknown, after: unknown, key = ""): DiffOp[] {
  if (typeof before !== "object" || before === null || typeof after !== "object" || after === null) {
    if (JSON.stringify(before) === JSON.stringify(after)) {
      return [{ kind: "unchanged", key, value: after }];
    }
    return [{ kind: "changed", key, before, after }];
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    if (JSON.stringify(before) === JSON.stringify(after)) {
      return [{ kind: "unchanged", key, value: after }];
    }
    return [{ kind: "changed", key, before, after }];
  }
  const b = before as Record<string, unknown>;
  const a = after as Record<string, unknown>;
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const out: DiffOp[] = [];
  for (const k of keys) {
    const path = key ? `${key}.${k}` : k;
    if (!(k in b)) out.push({ kind: "added", key: path, value: a[k] });
    else if (!(k in a)) out.push({ kind: "removed", key: path, value: b[k] });
    else if (typeof b[k] === "object" && b[k] !== null && typeof a[k] === "object" && a[k] !== null && !Array.isArray(a[k])) {
      out.push(...diff(b[k], a[k], path));
    } else if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) {
      out.push({ kind: "changed", key: path, before: b[k], after: a[k] });
    } else {
      out.push({ kind: "unchanged", key: path, value: a[k] });
    }
  }
  return out;
}

function formatValue(v: unknown): string {
  if (v === undefined) return "(undefined)";
  if (typeof v === "string") return v;
  return JSON.stringify(v, null, 2);
}

export function JsonDiff({ before, after, className }: { before: unknown; after: unknown; className?: string }) {
  const ops = diff(before ?? {}, after ?? {});
  const changes = ops.filter((o) => o.kind !== "unchanged");
  if (changes.length === 0) {
    return <div className={cn("text-xs text-fg-muted px-3 py-2", className)}>No changes.</div>;
  }
  return (
    <ul className={cn("panel divide-y divide-border text-xs font-mono", className)}>
      {changes.map((op, i) => (
        <li key={i} className="px-3 py-2">
          {op.kind === "added" && (
            <div>
              <span className="text-success">+ {op.key}</span>
              <pre className="mt-0.5 text-fg whitespace-pre-wrap break-words">{formatValue(op.value)}</pre>
            </div>
          )}
          {op.kind === "removed" && (
            <div>
              <span className="text-danger">- {op.key}</span>
              <pre className="mt-0.5 text-fg-muted line-through whitespace-pre-wrap break-words">{formatValue(op.value)}</pre>
            </div>
          )}
          {op.kind === "changed" && (
            <div>
              <span className="text-warning">~ {op.key}</span>
              <pre className="mt-0.5 text-danger whitespace-pre-wrap break-words">- {formatValue(op.before)}</pre>
              <pre className="text-success whitespace-pre-wrap break-words">+ {formatValue(op.after)}</pre>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
